/**
 * printRegistrationForm — Betera Training Center
 * ------------------------------------------------
 * Opens a clean, print-optimised A4 window with the client intake form
 * ("Ficha de Alta de Cliente"). Two modes:
 *   - Blank  : printClientIntakeForm()          → empty form for handwriting
 *   - Filled : printClientIntakeForm(client)    → pre-filled from a client record
 *
 * The form is rendered in its own window with self-contained CSS so the app's
 * dark theme never leaks into the printout. Spanish labels (client-facing).
 */

const LOGO_URL =
  'https://customer-assets.emergentagent.com/job_workout-central-9/artifacts/1boxowhf_logo%20btc.PNG';

const TERMS_CONTENT = `Declaro que los datos proporcionados son veraces y que he informado al entrenador de cualquier lesión, patología o condición médica relevante para la práctica de actividad física.

Soy consciente de que la práctica de ejercicio físico implica un riesgo inherente de lesión y acepto participar bajo mi propia responsabilidad, eximiendo a Betera Training Center y a su personal de cualquier responsabilidad derivada de un uso indebido de las instalaciones o del incumplimiento de las indicaciones profesionales recibidas.

Me comprometo a respetar las normas del centro y a seguir las indicaciones del entrenador durante las sesiones de entrenamiento.`;

const PRIVACY_CONTENT = `De conformidad con el Reglamento (UE) 2016/679 (RGPD), autorizo a Betera Training Center al tratamiento de mis datos personales con la finalidad de gestionar mi relación como cliente, la planificación de entrenamientos, el seguimiento deportivo y las comunicaciones relacionadas con el servicio.

Los datos no serán cedidos a terceros salvo obligación legal y se conservarán mientras se mantenga la relación contractual.

He sido informado de que puedo ejercer mis derechos de acceso, rectificación, supresión, limitación y oposición mediante solicitud al responsable del tratamiento.`;

const GENDER_MAP = { male: 'Hombre', female: 'Mujer', prefer_not_to_say: 'Prefiero no decirlo' };
const REFERRAL_MAP = {
  social_media: 'Redes sociales',
  recommendation: 'Recomendación',
  web_search: 'Búsqueda web',
  other: 'Otro',
};
const RELATIONSHIP_MAP = {
  spouse: 'Cónyuge / Pareja',
  parent: 'Padre / Madre',
  sibling: 'Hermano/a',
  child: 'Hijo/a',
  friend: 'Amigo/a',
  other: 'Otro',
};
const TIME_SLOTS = [
  { key: 'morning', label: 'Mañana' },
  { key: 'midday', label: 'Mediodía' },
  { key: 'afternoon', label: 'Tarde' },
  { key: 'evening', label: 'Noche' },
];
const DAYS = [
  { key: 'monday', label: 'Lunes' },
  { key: 'tuesday', label: 'Martes' },
  { key: 'wednesday', label: 'Miércoles' },
  { key: 'thursday', label: 'Jueves' },
  { key: 'friday', label: 'Viernes' },
  { key: 'saturday', label: 'Sábado' },
];

const esc = (v) => {
  if (v === null || v === undefined) return '';
  return String(v)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return esc(dateStr);
    return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return esc(dateStr);
  }
};

// A labelled write-in field. When a value exists it's printed; otherwise a blank line.
const field = (label, value, { span = 1 } = {}) => `
  <div class="field" style="grid-column: span ${span};">
    <div class="flabel">${esc(label)}</div>
    <div class="fvalue">${value ? `<span class="fval">${esc(value)}</span>` : '&nbsp;'}</div>
  </div>`;

const box = (checked, label) =>
  `<span class="opt"><span class="box">${checked ? 'X' : ''}</span>${esc(label)}</span>`;

export function buildIntakeFormHTML(client = null) {
  const c = client || {};
  const sched = c.schedule_preferences || {};
  const slots = Array.isArray(sched.time_slots) ? sched.time_slots : [];
  const days = Array.isArray(sched.days) ? sched.days : [];
  const isFilled = !!client;
  const title = isFilled ? 'Ficha de Cliente' : 'Ficha de Alta de Cliente';

  const feeLabel =
    c.booking_mode === 'monthly_fixed'
      ? 'Cuota mensual (€)'
      : c.booking_mode === 'booking_flex'
      ? 'Precio bono / sesión (€)'
      : 'Cuota / Bono (€)';

  const html = `<!doctype html>
<html lang="es">
<head>
<meta charset="utf-8" />
<title>${esc(title)}${isFilled && c.full_name ? ' — ' + esc(c.full_name) : ''}</title>
<style>
  @page { size: A4; margin: 12mm; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body { font: 12px/1.45 Arial, Helvetica, sans-serif; color: #111; background: #fff; }

  .toolbar { background:#1e293b; color:#fff; padding:10px 16px; display:flex; gap:8px; align-items:center; justify-content:flex-end; position:sticky; top:0; }
  .toolbar span { margin-right:auto; font-weight:600; }
  .toolbar button { font:600 13px Arial; padding:8px 16px; border:0; border-radius:6px; cursor:pointer; }
  .btn-print { background:#3b82f6; color:#fff; }
  .btn-close { background:#e2e8f0; color:#0f172a; }

  .sheet { max-width: 190mm; margin: 0 auto; padding: 8mm 4mm; }

  .head { display:flex; align-items:center; gap:16px; border-bottom:2px solid #111; padding-bottom:10px; margin-bottom:6px; }
  .head img { height:64px; width:64px; object-fit:contain; }
  .head .titles h1 { font-size:20px; margin:0; letter-spacing:.5px; }
  .head .titles p { margin:2px 0 0; font-size:12px; color:#444; }
  .head .meta { margin-left:auto; text-align:right; font-size:11px; color:#333; }
  .head .meta .num { font-size:14px; font-weight:700; color:#111; }

  .section { margin-top:14px; break-inside: avoid; }
  .section > h2 { font-size:12px; text-transform:uppercase; letter-spacing:1px; background:#111; color:#fff; padding:4px 8px; margin:0 0 8px; }

  .grid { display:grid; grid-template-columns: repeat(2, 1fr); gap:10px 16px; }
  .grid-3 { grid-template-columns: repeat(3, 1fr); }

  .field { min-height: 30px; }
  .flabel { font-size:10px; text-transform:uppercase; letter-spacing:.3px; color:#555; margin-bottom:2px; }
  .fvalue { border-bottom:1px solid #999; min-height:18px; padding:1px 2px; }
  .fval { font-size:13px; }

  .opts { display:flex; flex-wrap:wrap; gap:8px 18px; padding:2px 0; }
  .opt { display:inline-flex; align-items:center; gap:6px; font-size:12px; }
  .box { display:inline-block; width:14px; height:14px; border:1.5px solid #111; text-align:center; line-height:12px; font-size:11px; font-weight:700; }

  .legal { font-size:10.5px; color:#222; white-space:pre-line; border:1px solid #ccc; padding:8px 10px; margin-top:6px; background:#fafafa; }
  .legal h3 { font-size:11px; margin:0 0 4px; text-transform:uppercase; letter-spacing:.5px; }

  .notice { border:1px solid #b45309; background:#fffbeb; color:#7c2d12; padding:8px 10px; font-size:10.5px; margin-top:8px; }

  .sign { display:grid; grid-template-columns: 2fr 1fr; gap:24px; margin-top:22px; }
  .sign .line { border-top:1px solid #111; padding-top:4px; font-size:10px; color:#555; text-transform:uppercase; letter-spacing:.3px; }

  .foot { margin-top:14px; font-size:9px; color:#777; text-align:center; }

  @media print {
    .toolbar { display:none !important; }
    .sheet { padding:0; }
    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  }
</style>
</head>
<body>
  <div class="toolbar no-print">
    <span>${esc(title)}${isFilled ? ' (con datos)' : ' (en blanco)'}</span>
    <button class="btn-print" onclick="window.print()">Imprimir</button>
    <button class="btn-close" onclick="window.close()">Cerrar</button>
  </div>

  <div class="sheet">
    <div class="head">
      <img src="${LOGO_URL}" alt="Bétera Training Center" />
      <div class="titles">
        <h1>${esc(title)}</h1>
        <p>Bétera Training Center</p>
      </div>
      <div class="meta">
        ${c.member_number ? `<div class="num">Nº Socio: ${esc(c.member_number)}</div>` : ''}
        <div>Fecha de alta: ${isFilled && c.created_at ? formatDate(c.created_at) : '____ / ____ / ______'}</div>
      </div>
    </div>

    <div class="section">
      <h2>Datos personales</h2>
      <div class="grid">
        ${field('Nombre completo', c.full_name, { span: 2 })}
        ${field('Email', c.email)}
        ${field('Teléfono', c.phone)}
        ${field('Nacionalidad', c.nationality)}
        ${field('Dirección', c.address)}
        ${field('Código postal', c.postal_code)}
        ${field('Población', c.city)}
      </div>
    </div>

    <div class="section">
      <h2>Datos físicos</h2>
      <div class="grid grid-3">
        ${field('Fecha de nacimiento', isFilled ? formatDate(c.birth_date) : '')}
        ${field('Sexo', GENDER_MAP[c.gender] || '')}
        ${field('Altura (cm)', c.height)}
        ${field('Peso (kg)', c.weight)}
      </div>
      <div style="margin-top:6px; font-weight:700; font-size:11px;">Contacto de emergencia</div>
      <div class="grid grid-3" style="margin-top:4px;">
        ${field('Nombre', c.emergency_contact_name)}
        ${field('Relación', RELATIONSHIP_MAP[c.emergency_contact_relationship] || '')}
        ${field('Teléfono', c.emergency_contact_phone)}
      </div>
    </div>

    <div class="section">
      <h2>Información de entrenamiento</h2>
      <div class="grid" style="grid-template-columns:1fr;">
        ${field('Historial de lesiones / condiciones médicas', c.injury_history_note, { span: 1 })}
        ${field('¿Cómo nos conoció?', REFERRAL_MAP[c.referral_source] || '')}
        ${field('Objetivos de entrenamiento', c.training_objectives, { span: 1 })}
      </div>
    </div>

    <div class="section">
      <h2>Preferencias</h2>
      <div style="font-size:11px; margin-bottom:4px; color:#555; text-transform:uppercase; letter-spacing:.3px;">Modalidad</div>
      <div class="opts">
        ${box(c.booking_mode === 'monthly_fixed', 'Mensual (cuota fija)')}
        ${box(c.booking_mode === 'booking_flex', 'Por sesiones (bono)')}
      </div>
      <div class="grid" style="margin-top:8px;">
        ${field(feeLabel, c.monthly_fee != null && c.monthly_fee !== '' ? c.monthly_fee : '')}
      </div>
      <div style="font-size:11px; margin:10px 0 4px; color:#555; text-transform:uppercase; letter-spacing:.3px;">Franjas horarias</div>
      <div class="opts">
        ${TIME_SLOTS.map((s) => box(slots.includes(s.key), s.label)).join('')}
      </div>
      <div style="font-size:11px; margin:10px 0 4px; color:#555; text-transform:uppercase; letter-spacing:.3px;">Días preferidos</div>
      <div class="opts">
        ${DAYS.map((d) => box(days.includes(d.key), d.label)).join('')}
      </div>
    </div>

    <div class="section">
      <h2>Condiciones legales</h2>
      <div class="legal"><h3>Términos y condiciones del servicio</h3>${esc(TERMS_CONTENT)}</div>
      <div class="legal"><h3>Política de privacidad (RGPD)</h3>${esc(PRIVACY_CONTENT)}</div>
      <div class="opts" style="margin-top:8px;">
        ${box(!!c.terms_accepted, 'Acepto los términos y condiciones')}
        ${box(!!c.privacy_policy_accepted, 'Acepto la política de privacidad')}
      </div>
      <div class="notice">
        <strong>Aviso importante:</strong> Si padeces alguna lesión, enfermedad crónica o condición médica que pueda verse afectada por la práctica de ejercicio físico, informa previamente al entrenador antes de iniciar cualquier actividad.
      </div>
    </div>

    <div class="sign">
      <div class="line">Firma del cliente</div>
      <div class="line">Fecha</div>
    </div>

    <div class="foot">
      La contraseña de acceso a la app se asigna al crear la cuenta en el sistema — no se recoge en este documento.
    </div>
  </div>

  <script>
    window.addEventListener('load', function () {
      setTimeout(function () { try { window.focus(); window.print(); } catch (e) {} }, 400);
    });
  </script>
</body>
</html>`;

  return html;
}

export function printClientIntakeForm(client = null) {
  const html = buildIntakeFormHTML(client);
  const w = window.open('', '_blank', 'width=900,height=1000');
  if (!w) {
    alert('Permite las ventanas emergentes para imprimir la ficha.');
    return;
  }
  w.document.open();
  w.document.write(html);
  w.document.close();
}

export default printClientIntakeForm;
